import { useEffect, useState, useCallback, useRef } from "react";
import { ShieldCheck, Trash2, Loader2, Users, FileText, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoKey, setLogoKey] = useState(Date.now());
  const [logoExists, setLogoExists] = useState(true);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInput = useRef(null);

  const uploadLogo = async (f) => {
    if (!f) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/admin/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoKey(Date.now());
      setLogoExists(true);
      toast.success("Logo mis à jour. Il sera intégré à tous les prochains contrats.");
    } catch (e) {
      toast.error(formatApiError(e, "Échec de l'envoi du logo"));
    } finally {
      setLogoUploading(false);
    }
  };

  const deleteLogo = async () => {
    try {
      await api.delete("/admin/logo");
      setLogoExists(false);
      setLogoKey(Date.now());
      toast.success("Logo supprimé");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, t, s] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/templates"),
        api.get("/contracts/stats"),
      ]);
      setUsers(u.data);
      setTemplates(t.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e, "Chargement impossible"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setRole = async (userId, role) => {
    try {
      await api.patch(`/admin/users/${userId}`, { role });
      toast.success("Rôle mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteUser = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/admin/users/${deleting.user_id}`);
      toast.success(`Utilisateur ${deleting.email} supprimé`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-slate-700" /> Administration
        </h1>
        <p className="text-sm text-slate-500 mt-1">Gestion des utilisateurs, des modèles et vue globale.</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="templates" data-testid="admin-tab-templates">Modèles de contrats</TabsTrigger>
          <TabsTrigger value="stats" data-testid="admin-tab-stats">Statistiques</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left">
                    <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Nom</th>
                    <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                    <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Rôle</th>
                    <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u) => (
                    <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`admin-user-row-${u.user_id}`}>
                      <td className="px-5 py-3.5 font-semibold text-slate-900">{u.name}</td>
                      <td className="px-5 py-3.5 text-slate-600">{u.email}</td>
                      <td className="px-5 py-3.5">
                        {u.user_id === user.user_id ? (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-900 text-white">Administrateur (vous)</span>
                        ) : (
                          <Select value={u.role} onValueChange={(r) => setRole(u.user_id, r)}>
                            <SelectTrigger className="w-40" data-testid={`admin-role-select-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Agent</SelectItem>
                              <SelectItem value="admin">Administrateur</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        {u.user_id !== user.user_id && (
                          <button onClick={() => setDeleting(u)} data-testid={`admin-user-delete-${u.user_id}`}
                            className="p-2 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors duration-150">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mt-4 mb-4" data-testid="admin-logo-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="h-20 w-40 border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                {logoExists ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/logo?v=${logoKey}`} alt="Logo de l'entreprise"
                    className="max-h-16 max-w-36 object-contain" onError={() => setLogoExists(false)} data-testid="admin-logo-preview" />
                ) : (
                  <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="admin-logo-empty"><ImageOff className="h-4 w-4" /> Aucun logo</span>
                )}
              </div>
              <div className="flex-1">
                <p className="font-bold text-slate-900">Logo de l'entreprise</p>
                <p className="text-xs text-slate-500 mt-1">Intégré automatiquement en haut de chaque contrat généré (JPG ou PNG, max 5 Mo).</p>
                <div className="flex gap-2 mt-3">
                  <input ref={logoInput} type="file" accept="image/jpeg,image/jpg,image/png" className="hidden"
                    data-testid="admin-logo-input" onChange={(e) => { uploadLogo(e.target.files[0]); e.target.value = ""; }} />
                  <button onClick={() => logoInput.current?.click()} disabled={logoUploading} data-testid="admin-logo-upload-btn"
                    className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors duration-150 disabled:opacity-50">
                    {logoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Télécharger le logo
                  </button>
                  {logoExists && (
                    <button onClick={deleteLogo} data-testid="admin-logo-delete-btn"
                      className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-colors duration-150">
                      <Trash2 className="h-4 w-4" /> Supprimer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {templates.map((t) => (
              <div key={t.filename} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid={`admin-template-${t.type}`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`p-2 rounded-lg ${t.type === "professionnel" ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"}`}>
                    <FileText className="h-5 w-5" />
                  </span>
                  <p className="font-bold text-slate-900">
                    {t.type === "professionnel" ? "Contrat Professionnel" : "Contrat Personnel"}
                  </p>
                </div>
                <p className="text-xs text-slate-500">Fichier modèle officiel : {t.filename} ({Math.round(t.size / 1024)} Ko)</p>
                <p className="text-xs text-slate-500 mt-1">Le nom et le prénom du client sont insérés automatiquement dans ce modèle.</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="stats">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="admin-stat-users">
              <Users className="h-5 w-5 text-slate-400 mb-2" />
              <p className="text-2xl font-extrabold text-slate-900">{users.length}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Utilisateurs</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="admin-stat-total">
              <FileText className="h-5 w-5 text-slate-400 mb-2" />
              <p className="text-2xl font-extrabold text-slate-900">{stats?.total ?? 0}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Contrats totaux</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="admin-stat-pro">
              <p className="text-2xl font-extrabold text-blue-600">{stats?.professionnel ?? 0}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Professionnels</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="admin-stat-perso">
              <p className="text-2xl font-extrabold text-emerald-600">{stats?.personnel ?? 0}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Personnels</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="admin-user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le compte <strong>{deleting?.email}</strong> sera définitivement supprimé. Ses contrats existants seront conservés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-user-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} className="bg-rose-600 hover:bg-rose-700" data-testid="admin-user-delete-confirm">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
